import { useState, useEffect } from 'react';
import { Outlet, Navigate } from 'react-router';
import { useAccount, useDisconnect } from 'wagmi';
import { ShieldX, Loader2 } from 'lucide-react';
import { Sidebar } from '@/shared/ui/sidebar';
import { Header } from '@/shared/ui/header';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { isAdmin } from '@/shared/config';
import { orpc } from '@/shared/api/client';

export function Layout() {
  const { isConnected, address } = useAccount();
  const { disconnect } = useDisconnect();

  // State for DB admin check
  const [isDbAdmin, setIsDbAdmin] = useState(false);
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);

  // Check hardcoded list (sync)
  const isHardcodedAdmin = isAdmin(address);

  // Check DB admins (async)
  useEffect(() => {
    if (!address || isHardcodedAdmin) {
      setIsCheckingAdmin(false);
      return;
    }

    setIsCheckingAdmin(true);
    orpc.admins.check({ wallet: address })
      .then((result) => setIsDbAdmin(result.isAdmin))
      .catch(() => setIsDbAdmin(false))
      .finally(() => setIsCheckingAdmin(false));
  }, [address, isHardcodedAdmin]);

  // Redirect to connect page if not authenticated
  if (!isConnected) {
    return <Navigate to="/connect" replace />;
  }

  // Show loading while checking DB admin status
  if (isCheckingAdmin && !isHardcodedAdmin) {
    return (
      <div className="min-h-screen bg-[#fdfcfb] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#807f7e]" />
      </div>
    );
  }

  // Check admin access (hardcoded OR database)
  if (!isHardcodedAdmin && !isDbAdmin) {
    return (
      <div className="min-h-screen bg-[#fdfcfb] flex items-center justify-center p-6">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
              <ShieldX className="w-8 h-8 text-red-600" />
            </div>
            <CardTitle className="text-xl text-[#2b2b2b]">Access Denied</CardTitle>
            <CardDescription>
              Your wallet is not authorized to access the admin dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-[#807f7e] mb-1">Connected Wallet</p>
              <code className="text-sm text-[#2b2b2b] break-all">{address}</code>
            </div>
            <p className="text-sm text-[#807f7e] text-center">
              Please connect with an authorized admin wallet or contact the team for access.
            </p>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => disconnect()}
            >
              Disconnect Wallet
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#fdfcfb]">
      <Sidebar />
      <div className="pl-64">
        <Header />
        <main className="p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
